import type { Hotkeys } from "owa-hotkeys";
import shouldUseMonarchShortcuts from "owa-hotkeys/lib/utils/shouldUseMonarchShortcuts";
import { getCurrentCulture } from "owa-localize";
import { shouldUseLocalizedFormatting } from "./shouldUseLocalizedFormatting";

type ComposeShortcutLocale = "da" | "de" | "es-es" | "fr" | "it" | "nb" | "nn" | "pt-br" | "pt-pt" | "sv";
export type LocalizedComposeCommand = "bold" | "italic" | "underline" | "saveDraft";

const localizedComposeHotkeys: Record<ComposeShortcutLocale, Partial<Record<LocalizedComposeCommand, Hotkeys>>> = {
    da: {
        bold: "ctrl+f",
        italic: "ctrl+k",
    },
    de: {
        bold: "ctrl+shift+f",
        italic: "ctrl+shift+k",
        underline: "ctrl+u",
    },
    "es-es": {
        bold: "ctrl+n",
        italic: "ctrl+k",
        underline: "ctrl+s",
        saveDraft: "ctrl+g",
    },
    fr: {
        bold: "ctrl+g",
    },
    it: {
        bold: "ctrl+g",
        italic: "ctrl+i",
        underline: "ctrl+s",
        saveDraft: "ctrl+b",
    },
    nb: {
        bold: "ctrl+f",
        italic: "ctrl+k",
    },
    nn: {
        bold: "ctrl+f",
        italic: "ctrl+k",
    },
    "pt-br": {
        bold: "ctrl+n",
        underline: "ctrl+s",
        saveDraft: "ctrl+b",
    },
    "pt-pt": {
        bold: "ctrl+n",
        underline: "ctrl+s",
        saveDraft: "ctrl+g",
    },
    sv: {
        bold: "ctrl+f",
        italic: "ctrl+k",
    },
};

/** Resolves the localized compose hotkey, falling back to the supplied default. */
export function resolveComposeHotkey(command: LocalizedComposeCommand, defaultHotkey: Hotkeys, currentCulture?: string): Hotkeys {
    if (!shouldUseLocalizedFormatting()) {
        return defaultHotkey;
    }
    
    const culture = (currentCulture ?? getCurrentCulture()).toLowerCase();
    const language = culture.split("-")[0];
    const shortcutLocale = (localizedComposeHotkeys[culture as ComposeShortcutLocale] ? culture : language) as ComposeShortcutLocale;
    
    // German Monarch also supports Ctrl+Shift+U for Underline.
    if (shortcutLocale == "de" && command == "underline" && shouldUseMonarchShortcuts()) {
        return ["ctrl+u", "ctrl+shift+u"];
    }
    
    return localizedComposeHotkeys[shortcutLocale]?.[command] ?? defaultHotkey;
}
